// File: src/components/EventDetailView.js
// Public detail page for one event: when and where it runs, which classes are
// raced, which transponders work, and - if the organizer publishes it - who has
// entered.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Mail, Phone, Globe, Users, ArrowLeft, Radio, UserPlus, Trophy, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateForDisplay } from '../utils/utils';
import { isChampionship, championshipBadgeClass, useChampionshipLabel } from '../utils/championships';
import { transponderLabel, parseTransponderSet } from '../utils/transponders';
import { apiGet } from '../services/api';
import EntryFormModal from './EntryFormModal';
import { getCountry } from '../data/countries';
import { useCountryName } from '../utils/countryNames';

// The host of the entry address, so the button says where it goes. A visitor
// deserves to know they are leaving the site before they click, and the full URL
// is far too long for a button.
const externalEntryHost = (url) => {
  try {
    return new URL(url).host;
  } catch {
    return '';
  }
};

// Defence in depth for URLs that come from the API and become an href. The
// backend already rejects non-http(s) schemes on website and external_entry_url,
// but a javascript:/data: href would run on click, so anything that is not a
// plain web URL is dropped here too rather than rendered as a live link.
const safeHref = (url) => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

function EventDetailView() {
  const countryName = useCountryName();
  const { t } = useLanguage();
  const championshipLabel = useChampionshipLabel();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEntryForm, setShowEntryForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/public/events/${eventId}`);
      setEvent(data);
      setError('');
    } catch (err) {
      setError(err.status === 404 ? t('calendarEventNotFound') : (err.message || t('calendarFailedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const backLink = (
    <Link to="/calendar" className="inline-flex items-center gap-2 text-ink-muted hover:text-ink mb-6">
      <ArrowLeft className="w-4 h-4" />
      {t('calendarBack')}
    </Link>
  );

  // Only on the FIRST load. Entering the event reloads this page to refresh the
  // entrant list, and blanking the page for that would unmount the entry modal
  // along with it - taking a guest's manage link, which is shown exactly once
  // and is the only way back to their entry, with it.
  if (loading && !event) {
    return <div>{backLink}<p className="text-ink-muted">{t('calendarLoading')}</p></div>;
  }

  if (error || !event) {
    return (
      <div>
        {backLink}
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded">
          {error || t('calendarEventNotFound')}
        </div>
      </div>
    );
  }

  const transponders = parseTransponderSet(event.supported_transponders);
  const location = [event.venue, event.address, event.city].filter(Boolean).join(', ');

  return (
    <div>
      {backLink}

      {event.status === 'cancelled' && (
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-6">
          {t('eventCancelled')}
        </div>
      )}

      <div className="bg-paper border border-line rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <h1 className="text-3xl font-bold text-ink">{event.name}</h1>
          {isChampionship(event.championship) && (
            <span className={`text-sm font-semibold px-3 py-1 rounded flex items-center gap-2 ${championshipBadgeClass(event.championship)}`}>
              <Trophy className="w-4 h-4" />
              {championshipLabel(event.championship)}
            </span>
          )}
        </div>

        <div className="space-y-2 text-ink-muted">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            {formatDateForDisplay(event.start_date)}
            {event.end_date && ` – ${formatDateForDisplay(event.end_date)}`}
          </div>

          {location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              {location}
            </div>
          )}

          {/* Which country is running it. Only worth saying for a championship:
              every other event is on the calendar of the country it is in. */}
          {isChampionship(event.championship) && (
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 flex-shrink-0" />
              {t('championshipHostedIn')}:{' '}
              {(() => {
                const host = getCountry(event.country);
                return host ? `${host.flag} ${countryName(host)}` : event.country;
              })()}
            </div>
          )}

          {event.organizer && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 flex-shrink-0" />
              {event.organizer}
            </div>
          )}
        </div>

        {event.description && (
          <p className="text-ink mt-4 whitespace-pre-line">{event.description}</p>
        )}

        {/* entry_open is computed server-side from status and the entry window,
            so this site's own entry button never appears when the server would
            refuse it. */}
        {!event.external_entry && event.entry_open && (
          <button
            onClick={() => setShowEntryForm(true)}
            className="mt-4 bg-accent hover:opacity-90 text-accent-ink px-6 py-3 rounded font-semibold flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5" />
            {t('entryEnter')}
          </button>
        )}

        {/* An event entered elsewhere gets a link, and the entry window does not
            govern it: those dates describe THIS site's form, and the other
            system opens and closes on its own schedule. Hiding the link once our
            window passed left a live registration unreachable, which is what
            testing turned up. A cancelled event is the one exception - there is
            nothing to register for. */}
        {event.external_entry && event.status !== 'cancelled' && (
          <div className="mt-4">
            <a
              href={event.external_entry_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-accent hover:opacity-90 text-accent-ink px-6 py-3 rounded font-semibold inline-flex items-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              {t('entryRegisterExternally')} {externalEntryHost(event.external_entry_url)}
            </a>
            <p className="text-ink-muted text-sm mt-2">{t('entryHandledElsewhere')}</p>
          </div>
        )}

        {(event.contact_email || event.contact_phone || event.website) && (
          <div className="mt-4 pt-4 border-t border-line">
            <h3 className="text-ink-muted font-semibold text-sm mb-2">{t('eventContact')}</h3>
            <div className="space-y-1 text-ink-muted text-sm">
              {event.contact_email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${event.contact_email}`} className="hover:text-ink">{event.contact_email}</a>
                </div>
              )}
              {event.contact_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {event.contact_phone}
                </div>
              )}
              {event.website && (
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {/* External link from user-supplied content: noopener protects
                      window.opener, noreferrer keeps the URL out of Referer, and
                      safeHref refuses a javascript:/data: scheme - shown as plain
                      text rather than a live link if it is not a web URL. */}
                  {safeHref(event.website) ? (
                    <a
                      href={safeHref(event.website)}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="hover:text-ink break-all"
                    >
                      {event.website}
                    </a>
                  ) : (
                    <span className="break-all">{event.website}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-paper border border-line rounded-lg p-6">
          <h2 className="text-xl font-bold text-ink mb-4">{t('eventClasses')}</h2>

          {event.classes.length === 0 ? (
            <p className="text-ink-muted">{t('eventNoClasses')}</p>
          ) : (
            <div className="space-y-2">
              {event.classes.map(cls => {
                const full = cls.entry_limit && cls.entry_count >= cls.entry_limit;
                return (
                  <div key={cls.id} className="bg-paper-alt rounded px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-ink font-medium truncate">{cls.name}</div>
                      {cls.series_name && (
                        <div className="text-ink-muted text-sm truncate">{cls.series_name}</div>
                      )}
                    </div>
                    <div className="text-ink-muted text-sm whitespace-nowrap">
                      {full ? (
                        <span className="text-red-700 dark:text-red-400 font-semibold">
                          {t('eventEntryLimitReached')}
                        </span>
                      ) : (
                        <>
                          {cls.entry_count}
                          {cls.entry_limit ? ` / ${cls.entry_limit}` : ''}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-line">
            <h3 className="text-ink-muted font-semibold text-sm mb-2 flex items-center gap-2">
              <Radio className="w-4 h-4" />
              {t('eventSupportedTransponders')}
            </h3>
            {transponders.length === 0 ? (
              <p className="text-ink-muted text-sm">{t('eventNoTransponders')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {transponders.map(value => (
                  <span key={value} className="text-xs px-2 py-1 rounded bg-paper-alt text-ink-muted">
                    {transponderLabel(value)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-paper border border-line rounded-lg p-6">
          <h2 className="text-xl font-bold text-ink mb-4">{t('eventEntrantList')}</h2>

          {/* Nobody enters here, so there is no list to show - and an empty one
              would read as "nobody has entered yet", which is worse than saying
              where the entries actually are. */}
          {event.external_entry ? (
            <p className="text-ink-muted">{t('entryHandledElsewhere')}</p>
          ) : event.entries === null ? (
            <p className="text-ink-muted">{t('eventEntrantListHidden')}</p>
          ) : event.entries.length === 0 ? (
            <p className="text-ink-muted">{t('eventNoEntrants')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-ink-muted text-sm border-b border-line">
                    <th className="py-2 pr-3 font-semibold">{t('name')}</th>
                    <th className="py-2 pr-3 font-semibold">{t('eventClub')}</th>
                    <th className="py-2 font-semibold">{t('eventClasses')}</th>
                  </tr>
                </thead>
                <tbody>
                  {event.entries.map(entry => (
                    <tr key={entry.id} className="border-b border-line last:border-0">
                      <td className="py-2 pr-3 text-ink">
                        {entry.first_name} {entry.last_name}
                        {entry.car_number ? (
                          <span className="text-ink-muted font-mono-num"> #{entry.car_number}</span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3 text-ink-muted">{entry.club || ''}</td>
                      <td className="py-2 text-ink-muted">{entry.classes || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showEntryForm && (
        <EntryFormModal
          event={event}
          // Signed in? Bind the entry to that account and prefill from it.
          token={localStorage.getItem('token') || undefined}
          onClose={() => setShowEntryForm(false)}
          onEntered={load}
          // A new account changes what the rest of the app should show, and the
          // session was written straight to localStorage.
          onSignedIn={() => window.dispatchEvent(new Event('rc-session-changed'))}
        />
      )}
    </div>
  );
}

export default EventDetailView;
